import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { userAPI } from '../../services/api';
import { useAuth } from '../context/AuthContext';
import '../../styles/admin/DownlineManagement.css';

const DownlineManagement = () => {
    const { userId } = useParams();
    const navigate = useNavigate();
    const { user: currentUser } = useAuth();
    
    const [activeTab, setActiveTab] = useState('overview');
    const [downlineData, setDownlineData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filters, setFilters] = useState({
        role: '',
        search: '',
        status: '',
        dateRange: 'all'
    });
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 20,
        total: 0
    });

    useEffect(() => {
        loadDownlineData();
    }, [userId, activeTab, pagination.page, filters]);

    const loadDownlineData = async () => {
        try {
            setLoading(true);
            
            switch(activeTab) {
                case 'overview':
                    const overview = await userAPI.getDownlineOverview(userId);
                    setDownlineData(overview.data);
                    break;
                    
                case 'users':
                    const users = await userAPI.getDownlineDetails(userId, {
                        page: pagination.page,
                        limit: pagination.limit,
                        role: filters.role,
                        search: filters.search
                    });
                    setDownlineData(users.data);
                    break;
                    
                case 'withdrawals':
                    const withdrawals = await userAPI.getDownlineWithdrawals(userId, {
                        page: pagination.page,
                        limit: pagination.limit,
                        status: filters.status
                    });
                    setDownlineData(withdrawals.data);
                    break;
                    
                case 'transactions':
                    const transactions = await userAPI.getDownlineTransactions(userId, {
                        page: pagination.page,
                        limit: pagination.limit,
                        type: filters.type,
                        startDate: filters.startDate,
                        endDate: filters.endDate
                    });
                    setDownlineData(transactions.data);
                    break;
                    
                case 'balances':
                    const balances = await userAPI.getDownlineBalances(userId);
                    setDownlineData(balances.data);
                    break;
                    
                default:
                    break;
            }
            
            setError(null);
        } catch (error) {
            setError(error.response?.data?.message || 'Error loading data');
        } finally {
            setLoading(false);
        }
    };

    const handleFilterChange = (key, value) => {
        setFilters(prev => ({ ...prev, [key]: value }));
        setPagination(prev => ({ ...prev, page: 1 }));
    };

    const formatNumber = (num) => {
        if (!num && num !== 0) return '₹0';
        return `₹${num.toLocaleString('en-IN')}`;
    };

    const getRoleColor = (role) => {
        const colors = {
            admin: '#dc3545',
            super_master: '#fd7e14',
            master: '#007bff',
            agent: '#6f42c1',
            user: '#28a745'
        };
        return colors[role] || '#6c757d';
    };

    const getStatusBadge = (status) => {
        const badges = {
            pending: 'badge-warning',
            approved: 'badge-info',
            completed: 'badge-success',
            rejected: 'badge-danger'
        };
        return badges[status] || 'badge-secondary';
    };

    if (loading && !downlineData) {
        return <div className="loading">Loading downline data...</div>;
    }

    return (
        <div className="downline-management">
            <div className="header">
                <h1>Downline Management</h1>
                <button 
                    className="btn btn-secondary"
                    onClick={() => navigate(-1)}
                >
                    ← Back
                </button>
            </div>

            {/* Tabs */}
            <div className="tabs">
                <button 
                    className={`tab ${activeTab === 'overview' ? 'active' : ''}`}
                    onClick={() => setActiveTab('overview')}
                >
                    📊 Overview
                </button>
                <button 
                    className={`tab ${activeTab === 'users' ? 'active' : ''}`}
                    onClick={() => setActiveTab('users')}
                >
                    👥 Users
                </button>
                <button 
                    className={`tab ${activeTab === 'withdrawals' ? 'active' : ''}`}
                    onClick={() => setActiveTab('withdrawals')}
                >
                    💸 Withdrawals
                </button>
                <button 
                    className={`tab ${activeTab === 'transactions' ? 'active' : ''}`}
                    onClick={() => setActiveTab('transactions')}
                >
                    💰 Transactions
                </button>
                <button 
                    className={`tab ${activeTab === 'balances' ? 'active' : ''}`}
                    onClick={() => setActiveTab('balances')}
                >
                    ⚖️ Balances
                </button>
            </div>

            {/* Filters */}
            {(activeTab === 'users' || activeTab === 'withdrawals' || activeTab === 'transactions') && (
                <div className="filters-bar">
                    {activeTab === 'users' && (
                        <>
                            <select 
                                value={filters.role}
                                onChange={(e) => handleFilterChange('role', e.target.value)}
                            >
                                <option value="">All Roles</option>
                                <option value="super_master">Super Master</option>
                                <option value="master">Master</option>
                                <option value="agent">Agent</option>
                                <option value="user">User</option>
                            </select>
                            <input
                                type="text"
                                placeholder="Search by username/email/phone"
                                value={filters.search}
                                onChange={(e) => handleFilterChange('search', e.target.value)}
                            />
                        </>
                    )}
                    
                    {activeTab === 'withdrawals' && (
                        <select 
                            value={filters.status}
                            onChange={(e) => handleFilterChange('status', e.target.value)}
                        >
                            <option value="">All Status</option>
                            <option value="pending">Pending</option>
                            <option value="approved">Approved</option>
                            <option value="completed">Completed</option>
                            <option value="rejected">Rejected</option>
                        </select>
                    )}
                    
                    {activeTab === 'transactions' && (
                        <>
                            <select 
                                value={filters.type}
                                onChange={(e) => handleFilterChange('type', e.target.value)}
                            >
                                <option value="">All Types</option>
                                <option value="credit">Credit</option>
                                <option value="debit">Debit</option>
                                <option value="bet">Bet</option>
                                <option value="win">Win</option>
                            </select>
                            <input
                                type="date"
                                value={filters.startDate}
                                onChange={(e) => handleFilterChange('startDate', e.target.value)}
                                placeholder="Start Date"
                            />
                            <input
                                type="date"
                                value={filters.endDate}
                                onChange={(e) => handleFilterChange('endDate', e.target.value)}
                                placeholder="End Date"
                            />
                        </>
                    )}
                </div>
            )}

            {/* Error Display */}
            {error && (
                <div className="alert alert-danger">{error}</div>
            )}

            {/* Content based on active tab */}
            <div className="tab-content">
                {activeTab === 'overview' && downlineData && (
                    <div className="overview-tab">
                        {/* Target User Info */}
                        <div className="target-user-card">
                            <h2>Viewing: {downlineData.targetUser?.username}</h2>
                            <p>Role: <span className="role-badge" style={{ backgroundColor: getRoleColor(downlineData.targetUser?.role) }}>
                                {downlineData.targetUser?.role}
                            </span></p>
                        </div>

                        {/* Stats Cards */}
                        <div className="stats-grid">
                            <div className="stat-card">
                                <h3>Total Downline</h3>
                                <p className="stat-value">{downlineData.stats?.totalDownlineCount}</p>
                            </div>
                            <div className="stat-card">
                                <h3>Total Balance</h3>
                                <p className="stat-value">{formatNumber(downlineData.stats?.balanceSummary?.totalBalance)}</p>
                            </div>
                            <div className="stat-card">
                                <h3>Total Credits</h3>
                                <p className="stat-value">{formatNumber(downlineData.stats?.transactionSummary?.totalCredits)}</p>
                            </div>
                            <div className="stat-card">
                                <h3>Total Withdrawals</h3>
                                <p className="stat-value">{formatNumber(downlineData.stats?.withdrawalSummary?.reduce((sum, w) => sum + w.amount, 0))}</p>
                            </div>
                        </div>

                        {/* Withdrawal Stats by Status */}
                        <div className="withdrawal-stats">
                            <h3>Withdrawal Status</h3>
                            <div className="status-cards">
                                {downlineData.stats?.withdrawalSummary?.map(stat => (
                                    <div key={stat._id} className={`status-card ${stat._id}`}>
                                        <h4>{stat._id.toUpperCase()}</h4>
                                        <p className="count">{stat.count}</p>
                                        <p className="amount">{formatNumber(stat.amount)}</p>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Immediate Downline */}
                        <div className="immediate-downline">
                            <h3>Immediate Downline ({downlineData.immediateDownline?.length})</h3>
                            <table className="table">
                                <thead>
                                    <tr>
                                        <th>Username</th>
                                        <th>Role</th>
                                        <th>Balance</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {downlineData.immediateDownline?.map(user => (
                                        <tr key={user._id}>
                                            <td>{user.username}</td>
                                            <td>
                                                <span className="role-badge" style={{ backgroundColor: getRoleColor(user.role) }}>
                                                    {user.role}
                                                </span>
                                            </td>
                                            <td>{formatNumber(user.balance)}</td>
                                            <td>
                                                <button 
                                                    className="btn btn-sm btn-info"
                                                    onClick={() => navigate(`/downline/${user._id}`)}
                                                >
                                                    View Downline
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Recent Activities */}
                        <div className="recent-activities">
                            <h3>Recent Activities</h3>
                            <div className="activities-grid">
                                <div className="activity-column">
                                    <h4>Recent Transactions</h4>
                                    <table className="table">
                                        <thead>
                                            <tr>
                                                <th>User</th>
                                                <th>Type</th>
                                                <th>Amount</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {downlineData.recentActivities?.transactions?.slice(0, 5).map(t => (
                                                <tr key={t._id}>
                                                    <td>{t.userId?.username}</td>
                                                    <td><span className={`badge ${t.type}`}>{t.type}</span></td>
                                                    <td className={t.type === 'credit' || t.type === 'win' ? 'positive' : 'negative'}>
                                                        {t.type === 'credit' || t.type === 'win' ? '+' : '-'}{formatNumber(t.amount)}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                                <div className="activity-column">
                                    <h4>Recent Withdrawals</h4>
                                    <table className="table">
                                        <thead>
                                            <tr>
                                                <th>User</th>
                                                <th>Amount</th>
                                                <th>Status</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {downlineData.recentActivities?.withdrawals?.slice(0, 5).map(w => (
                                                <tr key={w._id}>
                                                    <td>{w.userId?.username}</td>
                                                    <td>{formatNumber(w.amount)}</td>
                                                    <td><span className={`badge ${getStatusBadge(w.status)}`}>{w.status}</span></td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {activeTab === 'users' && downlineData && (
                    <div className="users-tab">
                        <table className="table">
                            <thead>
                                <tr>
                                    <th>Username</th>
                                    <th>Email</th>
                                    <th>Phone</th>
                                    <th>Role</th>
                                    <th>Balance</th>
                                    <th>Downline</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {downlineData.users?.map(user => (
                                    <tr key={user._id}>
                                        <td>{user.username}</td>
                                        <td>{user.email}</td>
                                        <td>{user.phone}</td>
                                        <td>
                                            <span className="role-badge" style={{ backgroundColor: getRoleColor(user.role) }}>
                                                {user.role}
                                            </span>
                                        </td>
                                        <td>{formatNumber(user.balance)}</td>
                                        <td>{user.stats?.downlineCount || 0}</td>
                                        <td>
                                            <button 
                                                className="btn btn-sm btn-info"
                                                onClick={() => navigate(`/downline/${user._id}`)}
                                            >
                                                View Details
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>

                        {/* Pagination */}
                        {downlineData.totalPages > 1 && (
                            <div className="pagination">
                                <button 
                                    disabled={pagination.page === 1}
                                    onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                                >
                                    Previous
                                </button>
                                <span>Page {pagination.page} of {downlineData.totalPages}</span>
                                <button 
                                    disabled={pagination.page === downlineData.totalPages}
                                    onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                                >
                                    Next
                                </button>
                            </div>
                        )}
                    </div>
                )}

                {activeTab === 'withdrawals' && downlineData && (
                    <div className="withdrawals-tab">
                        {/* Withdrawal Stats Summary */}
                        <div className="stats-summary">
                            {downlineData.stats?.byStatus?.map(stat => (
                                <div key={stat._id} className={`stat-mini-card ${stat._id}`}>
                                    <h4>{stat._id.toUpperCase()}</h4>
                                    <p className="count">{stat.count}</p>
                                    <p className="amount">{formatNumber(stat.amount)}</p>
                                </div>
                            ))}
                        </div>

                        <table className="table">
                            <thead>
                                <tr>
                                    <th>Date</th>
                                    <th>User</th>
                                    <th>Amount</th>
                                    <th>Method</th>
                                    <th>Status</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {downlineData.withdrawals?.map(withdrawal => (
                                    <tr key={withdrawal._id}>
                                        <td>{new Date(withdrawal.createdAt).toLocaleString()}</td>
                                        <td>{withdrawal.userId?.username} ({withdrawal.userId?.role})</td>
                                        <td>{formatNumber(withdrawal.amount)}</td>
                                        <td>{withdrawal.paymentMethod}</td>
                                        <td>
                                            <span className={`badge ${getStatusBadge(withdrawal.status)}`}>
                                                {withdrawal.status}
                                            </span>
                                        </td>
                                        <td>
                                            <button className="btn btn-sm btn-info">View</button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {activeTab === 'transactions' && downlineData && (
                    <div className="transactions-tab">
                        {/* Transaction Totals Summary */}
                        <div className="totals-summary">
                            {downlineData.totals?.map(total => (
                                <div key={total._id} className={`total-card ${total._id}`}>
                                    <h4>{total._id.toUpperCase()}</h4>
                                    <p className="count">{total.count} transactions</p>
                                    <p className="amount">{formatNumber(total.amount)}</p>
                                </div>
                            ))}
                        </div>

                        <table className="table">
                            <thead>
                                <tr>
                                    <th>Date</th>
                                    <th>User</th>
                                    <th>Type</th>
                                    <th>Amount</th>
                                    <th>Description</th>
                                    <th>By</th>
                                </tr>
                            </thead>
                            <tbody>
                                {downlineData.transactions?.map(transaction => (
                                    <tr key={transaction._id}>
                                        <td>{new Date(transaction.createdAt).toLocaleString()}</td>
                                        <td>{transaction.userId?.username} ({transaction.userId?.role})</td>
                                        <td>
                                            <span className={`badge ${transaction.type}`}>
                                                {transaction.type}
                                            </span>
                                        </td>
                                        <td className={transaction.type === 'credit' || transaction.type === 'win' ? 'positive' : 'negative'}>
                                            {transaction.type === 'credit' || transaction.type === 'win' ? '+' : '-'}{formatNumber(transaction.amount)}
                                        </td>
                                        <td>{transaction.description}</td>
                                        <td>{transaction.createdBy?.username}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {activeTab === 'balances' && downlineData && (
                    <div className="balances-tab">
                        {/* Balance Summary */}
                        <div className="balance-summary">
                            <div className="summary-card">
                                <h3>Total Balance</h3>
                                <p className="big-number">{formatNumber(downlineData.summary?.totalBalance)}</p>
                            </div>
                            <div className="summary-card">
                                <h3>Average Balance</h3>
                                <p className="big-number">{formatNumber(downlineData.summary?.averageBalance)}</p>
                            </div>
                            <div className="summary-card">
                                <h3>Highest Balance</h3>
                                <p className="big-number">{formatNumber(downlineData.summary?.maxBalance)}</p>
                            </div>
                            <div className="summary-card">
                                <h3>Lowest Balance</h3>
                                <p className="big-number">{formatNumber(downlineData.summary?.minBalance)}</p>
                            </div>
                        </div>

                        {/* Balance by Role */}
                        <div className="balance-by-role">
                            <h3>Balance by Role</h3>
                            <div className="role-cards">
                                {Object.entries(downlineData.summary?.byRole || {}).map(([role, data]) => (
                                    <div key={role} className="role-card" style={{ borderLeft: `4px solid ${getRoleColor(role)}` }}>
                                        <h4>{role.replace('_', ' ').toUpperCase()}</h4>
                                        <p className="count">{data.count} users</p>
                                        <p className="amount">{formatNumber(data.totalBalance)}</p>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Top Users */}
                        <div className="top-users">
                            <h3>Top 5 Users by Balance</h3>
                            <table className="table">
                                <thead>
                                    <tr>
                                        <th>Username</th>
                                        <th>Role</th>
                                        <th>Balance</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {downlineData.summary?.topUsers?.map(user => (
                                        <tr key={user._id}>
                                            <td>{user.username}</td>
                                            <td>
                                                <span className="role-badge" style={{ backgroundColor: getRoleColor(user.role) }}>
                                                    {user.role}
                                                </span>
                                            </td>
                                            <td className="positive">{formatNumber(user.balance)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default DownlineManagement;